import { FunctionComponent } from 'react'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { BootstrapDialog } from 'components/BootstrapDialog'
import { RequiredStar } from 'components/RequiredStarSpan'
import { DialogCloseButton } from 'components/DialogCloseButton'
import { EBidProcurementMeasurement } from 'types/bids/bidProcurementItems'
import { IProcurement } from 'types/procurements'
import { FileUpload } from 'components/FileUpload'

const capitalize: Function = (text: string) => {
  return text.charAt(0).toLocaleUpperCase('az') + text.slice(1).toLocaleLowerCase()
}

interface FormData extends IProcurement {}

interface Props {
  handleClose: () => void
  workRate?: Partial<IProcurement>
  onAdd: (workRate: IProcurement) => void
}

export const DialogWorkRateForm: FunctionComponent<Props> = ({
  handleClose,
  workRate,
  onAdd,
}) => {
  const { t } = useTranslation()
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    control,
  } = useForm<FormData>({
    reValidateMode: 'onSubmit',
    defaultValues: {
      id: workRate ? workRate.id : '',
      name: workRate ? workRate.name : '',
      description: workRate ? workRate.description : '',
      measurement: workRate ? workRate.measurement : '',
      quantity: (workRate ? workRate.quantity : '') as number,
      procurementRequirementId: workRate ? workRate.procurementRequirementId : '',
      attachedFileId: workRate ? workRate.attachedFileId : '',
    },
  })

  const onSubmit = (data: FormData) => {
    // TODO: add API call then call onAdd with received data
    onAdd(data)
    handleClose()
  }

  return (
    <BootstrapDialog open onClose={handleClose}>
      <DialogTitle sx={{ m: 0, p: 2 }}>{t('procurement-plan')}</DialogTitle>

      <DialogCloseButton handleClose={handleClose} />

      <DialogContent dividers>
        <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
          <TextField
            label={
              <>
                {t('name')} <RequiredStar>*</RequiredStar>
              </>
            }
            variant="outlined"
            error={!!errors.name}
            {...register('name', { required: t('required') })}
          />
          {errors.name && (
            <FormHelperText error>{errors.name?.message}</FormHelperText>
          )}
        </FormControl>

        <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
          <InputLabel id="measurement-select-helper-label">
            {t('measurement')} <RequiredStar>*</RequiredStar>
          </InputLabel>

          <Controller
            name="measurement"
            control={control}
            rules={{ required: t('required') }}
            render={({ field }) => (
              <Select
                {...field}
                error={!!errors.measurement}
                labelId="measurement-select-helper-label"
                label={
                  <>
                    {t('measurement')} <RequiredStar>*</RequiredStar>
                  </>
                }
              >
                {Object.keys(EBidProcurementMeasurement).map((measurement) => (
                  <MenuItem value={measurement} key={measurement}>
                    {capitalize(t(measurement))}
                  </MenuItem>
                ))}
              </Select>
            )}
          />

          {errors.measurement && (
            <FormHelperText error>{errors.measurement?.message}</FormHelperText>
          )}
        </FormControl>

        <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
          <TextField
            label={
              <>
                {t('quantity')} <RequiredStar>*</RequiredStar>
              </>
            }
            variant="outlined"
            error={!!errors.quantity}
            {...register('quantity', { required: t('required') })}
          />

          {errors.quantity && (
            <FormHelperText error>{errors.quantity?.message}</FormHelperText>
          )}
        </FormControl>

        <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
          <TextField
            label={
              <>
                {t('description')} <RequiredStar>*</RequiredStar>
              </>
            }
            variant="outlined"
            multiline
            minRows={4}
            error={!!errors.description}
            {...register('description', { required: t('required') })}
          />

          {errors.description && (
            <FormHelperText error>{errors.description?.message}</FormHelperText>
          )}
        </FormControl>
        <Controller
          name="attachedFileId"
          control={control}
          render={({ field }) => (
            <FileUpload
              text={t('extra-document')}
              handleFile={(file) => {
                field.onChange(file.id)
              }}
              fileId={field.value}
              accept="image/png, image/gif, image/jpeg"
              onDelete={() => setValue('attachedFileId', undefined)}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button
          autoFocus
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          form="work-rate-form"
        >
          {t('add')}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  )
}
